// hooks/useAuth.js - Refactored with proper logging and encrypted storage for OTP sessions
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../shared/services/Index';
import { oauthService } from '../../../shared/services/auth/oauthService';
import { sessionManager } from '../../../shared/services/sessionManagement/sessionManager';
import { useUser } from './useUser';
import credentialVerificationService from '../../../shared/integration/services/CredentialVerificationService';
import logger from '../../../shared/services/logging/logger';
import { asyncStorage } from '../../../shared/services/storage/asyncStorageService';
import { secureStorage } from '../../../shared/utils/secureStorage';
import {
  validateAWSConfig,
  handleInvalidCredentials
} from '../utils/navigationHelpers';
import { integrationService } from '../../../shared/integration/services/IntegrationService';
import { tokenService } from '../../../shared/services/auth/tokenService';
import { resetAllStores } from '../../../store/resetAllStores';

export const useAuth = () => {
  const navigate = useNavigate();
  const { setUserData } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationNotifications, setVerificationNotifications] = useState([]);

  /**
   * Verify integration credentials after login
   */
  const verifyCredentialsAfterLogin = useCallback(async (organizationId) => {
    if (!organizationId) {
      logger.info('No organization ID, skipping credential verification');
      return;
    }

    try {
      logger.info('Starting post-login credential verification');

      // Pass callback that adds notifications to state immediately
      const verificationResult = await credentialVerificationService.verifyAllCredentials(
        organizationId,
        (notification) => {
          logger.warn('Credential verification failure detected', { notification });
          logger.debug('Adding notification to queue');

          // Add notification to state array immediately
          setVerificationNotifications(prev => {
            logger.debug('Current notifications count', { count: prev.length });
            logger.debug('Adding new notification', { newCount: prev.length + 1 });
            return [...prev, notification];
          });
        }
      );

      if (!verificationResult.success && verificationResult.hasFailures) {
        logger.warn('Some integration credentials failed verification');

        // Log summary
        const summary = credentialVerificationService.getSummary(verificationResult);
        logger.info('Verification summary', { summary });
      } else {
        logger.info('All integration credentials verified successfully');
      }
    } catch (error) {
      logger.trackError('Error during credential verification', error, { organizationId });
      // Don't block login on verification errors
    }
  }, []);

  /**
   * Navigate after login based on integration setup status and credential validity
   * Refactored to use helper functions for better maintainability
   */
  const navigateAfterLogin = useCallback(async (organizationId, userRoles = []) => {
    if (!organizationId) {
      logger.warn('No organization ID, proceeding to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }

    try {
      // Single integrations fetch — used for both setup check and credential validation
      const integrationsResult = await integrationService.getAllIntegrations(organizationId);

      if (!integrationsResult.success) {
        // API failure (e.g. network error) — don't block the user
        logger.warn('Could not fetch integrations, proceeding to dashboard');
        navigate('/dashboard', { replace: true });
        return;
      }

      const integrations = integrationsResult.data || [];

      // First-login check: if any integration has hasLoggedIn === false, show setup page
      const needsSetup = integrations.length === 0 ||
        integrations.some(i => i.hasLoggedIn === false);

      if (needsSetup) {
        logger.info('Redirecting to integration setup');
        navigate('/integration-setup', { replace: true });
        return;
      }

      // Validate AWS config (fire-and-forget, never blocks navigation)
      await validateAWSConfig(organizationId);

      // Check for invalid Atlassian/XRAY credentials using the already-fetched data
      const invalidIntegrations = integrations
        .filter(i => i.integrationType === 'ATLASSIAN' || i.integrationType === 'XRAY')
        .filter(i => {
          const hasCredentials =
            (i.emailSupplied && i.emailSupplied !== 'not_supplied') ||
            (i.apiTokenSupplied && i.apiTokenSupplied !== 'not_supplied') ||
            (i.clientIdSupplied && i.clientIdSupplied !== 'not_supplied') ||
            (i.clientSecretSupplied && i.clientSecretSupplied !== 'not_supplied');
          return hasCredentials && i.verificationStatus === 'invalid';
        })
        .map(i => ({ type: i.integrationType, reason: 'Supplied credentials are invalid', mandatory: false }));

      if (invalidIntegrations.length > 0) {
        handleInvalidCredentials(invalidIntegrations, userRoles, navigate);
        return;
      }

      logger.info('All credentials valid, proceeding to dashboard');
      navigate('/dashboard', { replace: true });

    } catch (error) {
      logger.trackError('Error during post-login navigation', error, { organizationId });
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  /**
   * Generate OTP for email login
   * Uses encrypted storage for OTP session data (OWASP A02 mitigation)
   */
  const generateOtp = useCallback(async (email, clientMetadata = {}) => {
    let isByocRedirect = false;
    try {
      setError(null);
      setLoading(true);

      const requestData = { email };
      if (Object.keys(clientMetadata).length > 0) {
        requestData.clientMetadata = clientMetadata;
      }

      const result = await authService.generateOtp(requestData);

      // BYOC user — redirect to hosted UI, keep loading=true so the button
      // stays in "Verifying..." state until the browser navigates away
      if (result.redirectToHostedUi && result.hostedUiUrl) {
        isByocRedirect = true;
        sessionStorage.setItem('cognito_auth_email', email);
        window.location.href = result.hostedUiUrl;
        return { success: true, byocRedirect: true };
      }

      // Store OTP session securely using encryption
      await secureStorage.setOTPSession(email, result.otpReference || result.session);

      return { success: true, data: result };
    } catch (err) {
      const errorMessage = err.message || 'Failed to generate OTP';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      // Don't clear loading on BYOC redirect — browser is navigating away
      if (!isByocRedirect) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Verify OTP and login
   * Retrieves OTP session from encrypted storage (OWASP A02 mitigation)
   */
  const verifyOtp = useCallback(async (email, otp) => {
    setError(null);
    setLoading(true);

    try {
      // Retrieve OTP session from encrypted storage
      const otpSessionData = await secureStorage.getOTPSession();
      if (!otpSessionData || !otpSessionData.otpSession) {
        throw new Error('Session expired. Please request a new OTP.');
      }

      // Verify email matches
      if (otpSessionData.email !== email) {
        logger.warn('Email mismatch in OTP verification');
        throw new Error('Session mismatch. Please request a new OTP.');
      }

      const response = await authService.login({
        email,
        otp,
        session: otpSessionData.otpSession
      });

      // Check for accessToken directly (no success wrapper)
      if (response && response.accessToken) {
        const organizations = response.organizations || [];
        const currentOrg = response.currentOrganization || organizations[0] || null;

        // Process roles
        let displayRole = '';
        if (response.roles) {
          if (Array.isArray(response.roles) && response.roles.length > 0) {
            displayRole = response.roles[0].replace('ROLE_', '');
          } else if (typeof response.roles === 'string') {
            displayRole = response.roles.replace('ROLE_', '');
          }
        }

        const userData = {
          userId: response.userId,
          name: response.name || 'User',
          surname: response.surname || '',
          email: response.email || email,
          roles: response.roles || [],
          role: displayRole || response.role || '',
          accessToken: response.accessToken,
          idToken: response.idToken,
          refreshToken: response.refreshToken || null,
          organizations: organizations,
          currentOrganization: currentOrg,
          currentOrganisationName: response.currentOrganisationName ||
            currentOrg?.name ||
            organizations[0]?.name ||
            'Your Organization',
          conversationsBucket: response.conversationsBucket || currentOrg?.conversationsBucket || '',
          fileManagementBucket: response.fileManagementBucket || currentOrg?.fileManagementBucket || '',
          organizationId: response.organizationId || currentOrg?.organizationId || '',
          divisionId: response.divisionId || '',
          groupId: response.groupId || ''
        };

        // Explicitly store tokens before the navigation check so getAuthHeaders()
        // can read them immediately — setUserData triggers the same setTokens call
        // but via Zustand state which may be async in the React cycle.
        tokenService.setTokens(
          userData.accessToken || null,
          userData.idToken || null,
          userData.refreshToken || null
        );
        tokenService.setEmail(userData.email);

        setUserData(userData, { isLogin: true });
        secureStorage.clearOTPSession();

        await navigateAfterLogin(userData.organizationId, userData.roles);

        return { success: true, userData };
      } else {
        throw new Error('Incorrect OTP. Resend an OTP.');
      }
    } catch (err) {
      const errorMessage = err.message || 'Verification failed. Please try again.';
      setError(errorMessage);
      setLoading(false); // Only set loading false on error
      return { success: false, error: errorMessage };
    }
    // Note: No finally block - we don't want to set loading false on success
    // because the page will navigate/reload anyway
  }, [setUserData, navigateAfterLogin]);

  /**
   * Register new user
   */
  const register = useCallback(async (registrationData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authService.register(registrationData);
      
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initiate Microsoft login
   */
  const loginWithMicrosoft = useCallback(async (email = null) => {
    try {
      setError(null);
      setLoading(true);
      await oauthService.initiateMicrosoftLogin(email);
    } catch (err) {
      const errorMessage = err.message || 'Failed to initiate Microsoft login';
      setError(errorMessage);
      setLoading(false);
    }
  }, []);

  /**
   * Initiate Google login
   */
  const loginWithGoogle = useCallback(async (email = null) => {
    try {
      setError(null);
      setLoading(true);
      await oauthService.initiateGoogleLogin(email);
    } catch (err) {
      const errorMessage = err.message || 'Failed to initiate Google login';
      setError(errorMessage);
      setLoading(false);
    }
  }, []);

  /**
   * Handle OAuth callback
   */
  const handleOAuthCallback = useCallback(async (code, provider = 'unknown', options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await oauthService.processAuthCode(code, provider, options);
      
      if (response.success && response.data) {
        const userData = {
          userId: response.data.userId,
          name: response.data.name || 'User',
          surname: response.data.surname || '',
          email: (response.data.email || '').toLowerCase(),
          roles: response.data.roles || [],
          role: Array.isArray(response.data.roles)
            ? response.data.roles[0]?.replace('ROLE_', '')
            : (response.data.role || 'USER'),
          accessToken: response.data.accessToken,
          idToken: response.data.idToken,
          organizations: response.data.organizations || [],
          currentOrganization: response.data.currentOrganization || null,
          currentOrganisationName: response.data.currentOrganisationName || 'Your Organization',
          conversationsBucket: response.data.conversationsBucket || '',
          fileManagementBucket: response.data.fileManagementBucket || '',
          organizationId: response.data.organizationId || '',
          divisionId: response.data.divisionId || '',
          groupId: response.data.groupId || ''
        };

        tokenService.setTokens(
          userData.accessToken || null,
          userData.idToken || null,
          userData.refreshToken || null
        );
        tokenService.setEmail(userData.email);

        setUserData(userData, { isLogin: true });

        // Navigate based on integration setup status and credential validity
        // Pass roles to navigateAfterLogin so it can check admin status
        await navigateAfterLogin(userData.organizationId, userData.roles);
        
        return { success: true, userData };
      } else {
        const errorMessage = response.error || `${provider} authentication failed`;
        setError(errorMessage);
        
        sessionStorage.setItem(`${provider}_auth_failed`, 'true');
        sessionStorage.setItem(`${provider}_error_message`, errorMessage);
        
        navigate(`/?${provider}_failed=true`);
        
        setLoading(false); // Only set false on error
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = err.message || `${provider} authentication failed`;
      setError(errorMessage);
      
      sessionStorage.setItem(`${provider}_auth_failed`, 'true');
      sessionStorage.setItem(`${provider}_error_message`, errorMessage);
      
      navigate(`/?${provider}_failed=true`);
      
      setLoading(false); // Only set false on error
      return { success: false, error: errorMessage };
    }
    // Note: No finally block - we don't want to set loading false on success
    // because the page will navigate/reload anyway
  }, [setUserData, navigate, navigateAfterLogin]);

  /**
   * Resend OTP
   * Uses encrypted storage for OTP session data (OWASP A02 mitigation)
   */
  const resendOtp = useCallback(async (email, isManualResend = false) => {
    try {
      setError(null);

      const result = await authService.generateOtp({
        email,
        clientMetadata: isManualResend ? { action: 'resend' } : {}
      });

      // Store OTP session securely using encryption
      const otpSession = result.session || result.otpReference;
      if (otpSession) {
        await secureStorage.setOTPSession(email, otpSession);
      }

      return { success: true, data: result };
    } catch (error) {
      const errorMessage = error.message || 'Failed to resend OTP';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * Complete logout with comprehensive cleanup including cache clearing
   * Clears encrypted storage and OTP sessions (OWASP A02 mitigation)
   */
  const logout = useCallback(async () => {
    try {
      // 1. Clear all in-memory caches FIRST (before clearing storage)
      try {
        const { default: cacheManager } = await import('../../../shared/services/sessionManagement/cacheManager');
        cacheManager.clearAllCaches();
      } catch (error) {
        // Continue with logout even if cache clearing fails
      }

      // 2. Clear all axios HTTP response caches (critical for preventing stale data)
      try {
        const { clearAllCache } = await import('../../../shared/config/cachedAxiosConfig');
        clearAllCache();
      } catch (error) {
        // Continue with logout even if axios cache clearing fails
      }

      // 3. Clear session manager
      await sessionManager.clearSession();

      // 4. Clear auth service data
      authService.logout();

      // 5. Clear user context
      setUserData(null);

      // 6. Reset all Zustand stores (prevents previous user data leaking)
      resetAllStores();

      // 7. Clear ALL storage including encrypted data
      sessionStorage.clear();
      asyncStorage.clear();

      // 8. Clear encrypted OTP session
      secureStorage.clearOTPSession();

      setError(null);

      window.dispatchEvent(new CustomEvent('userLoggedOut', {
        detail: { timestamp: new Date().toISOString() }
      }));

      navigate('/', { replace: true });

    } catch (error) {
      // Ensure storage and stores are cleared even if something above threw
      sessionStorage.clear();
      asyncStorage.clear();
      secureStorage.clearOTPSession();
      setUserData(null);
      resetAllStores();
      navigate('/', { replace: true });
    }
  }, [setUserData, navigate]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 🔥 Clear all verification notifications
   */
  const clearVerificationNotifications = useCallback(() => {
    setVerificationNotifications([]);
  }, []);

  /**
   * 🔥 Remove a single notification by index (when user dismisses it)
   */
  const removeVerificationNotification = useCallback((index) => {
    setVerificationNotifications(prev => prev.filter((_, i) => i !== index));
  }, []);

  return {
    // State
    loading,
    error,
    verificationNotifications,
    
    // Actions
    generateOtp,
    verifyOtp,
    register,
    loginWithMicrosoft,
    loginWithGoogle,
    handleOAuthCallback,
    resendOtp,
    logout,
    clearError,
    clearVerificationNotifications,
    removeVerificationNotification
  };
};